/**
 * Pure map geometry / layer helpers extracted from App.js (Épico 39 / #6).
 */
import {
  DEFAULT_RAIL_WINDOW_FRACTION,
  getRailLayerOpacity,
  RAIL_DIMMED_OPACITY,
  RAIL_HIGHLIGHT_OPACITY,
} from './features/pulseRail';

export const normalizeCoords = ([lat, lon]) => (lat < -90 || lat > 90 ? [lon, lat] : [lat, lon]);

export const NORMAL_ROUTE_STYLES = {
  health: { color: '#2ecc71', weight: 2, opacity: 0.65, lineCap: 'butt', lineJoin: 'round' },
  walking: { color: '#e67e22', weight: 2, opacity: 0.65, lineCap: 'butt', lineJoin: 'round' },
  running: { color: '#e74c3c', weight: 2, opacity: 0.65, lineCap: 'butt', lineJoin: 'round' },
  cycling: { color: '#3498db', weight: 2, opacity: 0.65, lineCap: 'butt', lineJoin: 'round' },
  transport: { color: '#9b59b6', weight: 2, opacity: 0.65, lineCap: 'butt', lineJoin: 'round' },
  car: { color: '#95a5a6', weight: 2, opacity: 0.65, lineCap: 'butt', lineJoin: 'round' },
  google_maps: { color: '#00bcd4', weight: 2, opacity: 0.65, lineCap: 'butt', lineJoin: 'round' },
};

export const ACTIVE_ROUTE_STYLE = { weight: 2, opacity: 1, lineCap: 'butt', lineJoin: 'round' };
export const DIMMED_ROUTE_STYLE = { weight: 1, opacity: 0.12 };

export const CHECKIN_CLUSTER_OPTIONS = Object.freeze({
  radius: 54,
  maxZoom: 16,
  minPoints: 2,
});

export const buildCheckinClusterFeatures = (checkins = []) => (
  checkins.reduce((features, checkin, checkinIndex) => {
    const [lat, lon] = normalizeCoords([Number(checkin?.lat), Number(checkin?.lon)]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return features;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return features;

    features.push({
      type: 'Feature',
      properties: { checkinIndex },
      geometry: { type: 'Point', coordinates: [lon, lat] },
    });
    return features;
  }, [])
);

export const getClusterMarkerSize = (pointCount) => (
  Math.round(Math.min(48, Math.max(30, 26 + (Math.log2(Math.max(2, pointCount)) * 3))))
);

export const isMapLayerVisible = (isolatedLayerKey, layerKey) => (
  !isolatedLayerKey || isolatedLayerKey === layerKey
);

export const getRouteDisplayStyle = (baseStyle, isActive, hasActiveRoute) => {
  if (isActive) return { ...baseStyle, ...ACTIVE_ROUTE_STYLE };
  if (hasActiveRoute) return { ...baseStyle, ...DIMMED_ROUTE_STYLE };
  return baseStyle;
};

export const applyRailHighlightToLayer = (layer, date, highlightPosition, range) => {
  if (!layer?.setStyle) return;
  const base = layer._railHighlightBase;
  if (!base) return;
  let mode = 'base';
  if (highlightPosition != null) {
    const opacity = getRailLayerOpacity(date, highlightPosition, range, DEFAULT_RAIL_WINDOW_FRACTION);
    if (opacity !== RAIL_HIGHLIGHT_OPACITY) mode = 'dim';
  }
  if (layer._railHighlightMode === mode) return;
  layer._railHighlightMode = mode;
  if (mode === 'base') {
    layer.setStyle(base);
    return;
  }
  layer.setStyle({
    ...base,
    opacity: RAIL_DIMMED_OPACITY,
    fillOpacity: base.fillOpacity != null ? RAIL_DIMMED_OPACITY : undefined,
  });
};

/** Escape untrusted labels before Leaflet popup HTML interpolation. */
export const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}[char]));

export const GOOGLE_MAPS_MAX_POINT_GAP_KM = 2;
export const MOVES_MAX_POINT_GAP_KM_BY_TYPE = Object.freeze({
  walking: 0.3,
  car: 1,
});

export const getRoutePoints = (route) => {
  if (!Array.isArray(route)) return [];

  return route
    .filter((point) => point && point.lat !== undefined && point.lon !== undefined)
    .map((point) => normalizeCoords([Number(point.lat), Number(point.lon)]))
    .filter(([lat, lon]) => Number.isFinite(lat) && Number.isFinite(lon));
};

/** Cap vertices drawn on the map (full geometry stays in route items). */
export const MAX_DISPLAY_ROUTE_POINTS = 250;

export const simplifyLatLngs = (points, maxPoints = MAX_DISPLAY_ROUTE_POINTS) => {
  if (!Array.isArray(points) || points.length <= maxPoints) return points;
  if (maxPoints < 2) return points.slice(0, maxPoints);
  const step = Math.ceil((points.length - 1) / (maxPoints - 1));
  const out = [];
  for (let i = 0; i < points.length - 1; i += step) {
    out.push(points[i]);
  }
  out.push(points[points.length - 1]);
  return out;
};

/** Single polyline [[lat,lon],...] or MultiPolyline [[[lat,lon],...], ...]. */
export const simplifyDrawableRoute = (drawable, maxPoints = MAX_DISPLAY_ROUTE_POINTS) => {
  if (!Array.isArray(drawable) || drawable.length === 0) return drawable;
  if (Array.isArray(drawable[0]?.[0])) {
    return drawable.map((segment) => simplifyLatLngs(segment, maxPoints));
  }
  return simplifyLatLngs(drawable, maxPoints);
};

const degreesToRadians = (value) => (value * Math.PI) / 180;

export const getPointDistanceKm = ([startLat, startLon], [endLat, endLon]) => {
  const latitudeDelta = degreesToRadians(endLat - startLat);
  const longitudeDelta = degreesToRadians(endLon - startLon);
  const startLatitude = degreesToRadians(startLat);
  const endLatitude = degreesToRadians(endLat);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(startLatitude) * Math.cos(endLatitude) * Math.sin(longitudeDelta / 2) ** 2;
  const clampedHaversine = Math.min(1, Math.max(0, haversine));

  return 6371.0088 * 2 * Math.atan2(
    Math.sqrt(clampedHaversine),
    Math.sqrt(1 - clampedHaversine)
  );
};

export const splitRoutePointsByDistance = (
  points,
  maxGapKm = GOOGLE_MAPS_MAX_POINT_GAP_KM
) => {
  if (!Array.isArray(points) || points.length < 2) return [];

  const segments = [];
  let currentSegment = [points[0]];

  for (let index = 1; index < points.length; index += 1) {
    const point = points[index];
    const previousPoint = points[index - 1];

    if (getPointDistanceKm(previousPoint, point) <= maxGapKm) {
      currentSegment.push(point);
      continue;
    }

    if (currentSegment.length >= 2) segments.push(currentSegment);
    currentSegment = [point];
  }

  if (currentSegment.length >= 2) segments.push(currentSegment);
  return segments;
};
